import React, { SyntheticEvent } from 'react'
import { isEmpty } from 'lodash'

import { BulletLabel, Skeleton } from 'components'
import { Row, TBody, ActionEventTable } from 'components/Table'
import { setValue } from 'helpers/common'
import { sortProductCategories } from 'features/StockKeepingUnit'

import type { IStockKeepingUnitSingleProps } from 'features/StockKeepingUnit'
import type { IColumn } from 'types'

interface Props {
  columns: IColumn[]
  stockKeepingUnits: IStockKeepingUnitSingleProps[]
  showMenu: boolean
  loading: boolean
  isError: boolean
  onClickRow: (id: string) => (e: SyntheticEvent) => void
}

const SkeletonListOdd: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton />,
  name: <Skeleton.whiteSkeleton width='351px' />,
  brand: <Skeleton.whiteSkeleton width='63px' />,
  category: <Skeleton.whiteSkeleton width='244px' />,
}

const SkeletonListEven: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton width='157px' />,
  name: <Skeleton.whiteSkeleton width='259px' />,
  brand: <Skeleton.whiteSkeleton width='90px' />,
  category: <Skeleton.whiteSkeleton width='150px' />,
}

const StockKeepingUnitList: React.FC<Props> = (props: Props) => {
  const { columns, loading, isError, onClickRow, stockKeepingUnits } = props

  const list: Record<string, React.ReactNode>[] = stockKeepingUnits.map(
    (item) => {
      return {
        no: item.no,
        name: item.name,
        code: setValue(item.skuCode),
        brand: setValue(item.brand),
        category: sortProductCategories(item.categories),
        isActive: <BulletLabel isActive={Boolean(item.isActive)} />,
      }
    },
  )

  if (loading) {
    return (
      <Skeleton.SkeletonList
        columns={columns}
        rowOdd={SkeletonListOdd}
        rowEven={SkeletonListEven}
      />
    )
  }

  if (isEmpty(stockKeepingUnits) || isError) {
    return <ActionEventTable colSpan={columns.length} isNotFound />
  }

  return (
    <TBody>
      {list.map((data, index) => (
        <Row
          data-testid={`sku-row-${stockKeepingUnits[index].id}`}
          key={index}
          columns={columns}
          data={data}
          onClick={onClickRow(`${stockKeepingUnits[index].id}`)}
          position='left'
        />
      ))}
    </TBody>
  )
}

export default StockKeepingUnitList
